export interface GoogleApiCredentials {
  clientId: string;
  clientSecret: string;
}

export interface GoogleDriveConfig {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
  credentials: GoogleApiCredentials;
}

class GoogleDriveService {
  private config: GoogleDriveConfig | null = null;
  private userCredentials: GoogleApiCredentials | null = null;

  constructor() {
    this.loadConfig();
    this.loadUserCredentials();
  }

  private loadConfig(): void {
    const stored = localStorage.getItem('godmode_google_drive_config');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        this.config = {
          ...parsed,
          expiresAt: new Date(parsed.expiresAt)
        };
      } catch (error) {
        console.error('Failed to load Google Drive config:', error);
      }
    }
  }

  private loadUserCredentials(): void {
    const stored = localStorage.getItem('godmode_google_api_credentials');
    if (stored) {
      try {
        this.userCredentials = JSON.parse(stored);
      } catch (error) {
        console.error('Failed to load Google API credentials:', error);
      }
    }
  }

  saveUserCredentials(credentials: GoogleApiCredentials): void {
    this.userCredentials = credentials;
    localStorage.setItem('godmode_google_api_credentials', JSON.stringify(credentials));
  }

  getUserCredentials(): GoogleApiCredentials | null {
    return this.userCredentials;
  }

  hasUserCredentials(): boolean {
    return !!this.userCredentials?.clientId && !!this.userCredentials?.clientSecret;
  }

  saveConfig(config: GoogleDriveConfig): void {
    this.config = config;
    localStorage.setItem('godmode_google_drive_config', JSON.stringify(config));
  }

  isConfigured(): boolean {
    return !!this.config?.accessToken && this.hasUserCredentials();
  }

  async getValidAccessToken(): Promise<string> {
    if (!this.config) {
      throw new Error('Google Drive not configured');
    }

    // Check if token is expired
    if (this.config.expiresAt <= new Date()) {
      await this.refreshAccessToken();
    }

    return this.config.accessToken;
  }

  private async refreshAccessToken(): Promise<void> {
    if (!this.config?.refreshToken) {
      throw new Error('No refresh token available');
    }

    if (!this.userCredentials) {
      throw new Error('Google API credentials not configured');
    }

    const response = await fetch('/api/auth/refresh-google-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshToken: this.config.refreshToken,
        clientId: this.userCredentials.clientId,
        clientSecret: this.userCredentials.clientSecret
      })
    });

    if (!response.ok) {
      throw new Error('Failed to refresh Google token');
    }

    const tokens = await response.json();
    this.saveConfig({
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token || this.config.refreshToken,
      expiresAt: new Date(Date.now() + tokens.expires_in * 1000),
      credentials: this.userCredentials
    });
  }

  getAuthorizationUrl(redirectUri: string, state: string = 'google_drive_setup'): string {
    if (!this.userCredentials) {
      throw new Error('Google API credentials not configured');
    }

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', this.userCredentials.clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'openid email profile https://www.googleapis.com/auth/drive.file');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');

    return authUrl.toString();
  }

  async exchangeCodeForTokens(code: string, redirectUri: string): Promise<{access_token: string; refresh_token: string; expires_in: number}> {
    if (!this.userCredentials) {
      throw new Error('Google API credentials not configured');
    }

    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        client_id: this.userCredentials.clientId,
        client_secret: this.userCredentials.clientSecret,
        code,
        grant_type: 'authorization_code',
        redirect_uri: redirectUri
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('Token exchange failed:', error);
      throw new Error('Failed to exchange code for tokens');
    }

    return response.json();
  }

  async uploadBackupFile(filename: string, content: string): Promise<string> {
    const accessToken = await this.getValidAccessToken();

    // Create file metadata
    const metadata = {
      name: filename,
      parents: ['appDataFolder'] // Store in app-specific folder
    };

    // Upload file in two steps: metadata first, then content
    const metadataResponse = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(metadata)
    });

    if (!metadataResponse.ok) {
      throw new Error(`Failed to initialize upload: ${metadataResponse.status}`);
    }

    const uploadUrl = metadataResponse.headers.get('Location');
    if (!uploadUrl) {
      throw new Error('No upload URL returned');
    }

    // Upload file content
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      body: content
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.status}`);
    }

    const result = await uploadResponse.json();
    return result.id;
  }

  async listBackupFiles(): Promise<Array<{id: string; name: string; createdTime: string; size: string}>> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&q=${encodeURIComponent("name contains 'godmode-backup'")}&fields=files(id,name,createdTime,size)&orderBy=createdTime desc`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to list files: ${response.status}`);
    }

    const result = await response.json();
    return result.files || [];
  }

  async downloadBackupFile(fileId: string): Promise<string> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    return response.text();
  }

  async deleteBackupFile(fileId: string): Promise<void> {
    const accessToken = await this.getValidAccessToken();

    const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Delete failed: ${response.status}`);
    }
  }
}

export const googleDriveService = new GoogleDriveService();